import React, { useState, useEffect } from 'react';
import { Plus, Rocket, Target, DollarSign, Palette, ShoppingCart, TrendingUp, Clock, Settings, Lightbulb, Zap, BarChart3, Users, TrendingDown } from 'lucide-react';
import { Link } from "react-router-dom";

const ABTestDataCollection = ({
    title = "Your A/B Test Platform is Ready",
    subtitle = "Start creating data-driven experiments to optimize your store's performance. Your first test can be live in minutes and start generating insights immediately.",
    icon = "🚀"
}) => {

    return (
        <div className="mx-auto py-6 px-4 md:px-5 min-h-screen">
            {/* Header with Create Button */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-2">
                        Element A/B Testing Dashboard
                    </h1>
                    <p className="text-slate-600 text-base">
                        Create and manage experiments to optimize your store's performance
                    </p>
                </div>
                <Link 
                    to="/create-element-test" 
                    className="w-full md:w-auto flex items-center justify-center gap-2 bg-black text-white px-5 py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors duration-200"
                >
                    <Plus size={18} />
                    Create Test
                </Link>
            </div>

            {/* Main Status Card */}
            <div className="relative bg-white rounded-3xl p-8 md:p-12 shadow-2xl mb-8 overflow-hidden">
                {/* Animated gradient top border */}
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-600 via-purple-600 via-red-500 to-emerald-500 bg-[length:300%_300%] animate-[gradientShift_3s_ease_infinite]"></div>

                <div className="text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full text-sm font-medium shadow-lg shadow-blue-500/30 mb-8">
                        <Zap size={16} />
                        Everything is configured and ready to go
                    </div>

                    <h1 className="flex flex-col md:flex-row items-center justify-center gap-3 text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4 leading-tight">
                        <Rocket size={32} className="text-blue-600" />
                        {title}
                    </h1>
                    <p className="text-lg text-slate-600 mb-6 leading-relaxed max-w-3xl mx-auto">
                        {subtitle}
                    </p>

                    {/* Flashing Launch A/B Test Button */}
                    <div className="mt-6 flex justify-center">
                        <Link 
                            to="/create-element-test" 
                            className="group relative inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white px-9 py-4 rounded-full text-lg font-bold shadow-lg shadow-blue-500/40 hover:shadow-xl hover:shadow-blue-500/60 hover:-translate-y-1 transition-all duration-300 mb-10 overflow-hidden animate-[flashyPulse_2s_ease-in-out_infinite] hover:[animation-play-state:paused]"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                            <Rocket size={20} />
                            Launch A/B Test
                        </Link>
                    </div>
                </div>
            </div>

            {/* Value Proposition */}
            <h2 className="flex flex-col md:flex-row items-center justify-center gap-3 text-2xl md:text-3xl font-bold text-slate-800 my-8 md:my-12">
                <Target size={28} />
                What You Can Test to Boost Revenue
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                {/* Pricing Strategies Card */}
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 border-2 border-transparent hover:border-blue-600">
                    <div className="w-15 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-purple-600 to-purple-400 mb-5 transition-transform duration-300 hover:scale-110">
                        <DollarSign size={28} className="text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-4">
                        Pricing Strategies
                    </h3>
                    <ul className="space-y-2 mb-5 list-none p-0">
                        <li className="flex items-start gap-3 text-slate-700 text-[15px]">
                            <span className="text-base mt-0.5">✨</span>
                            Test different price points and formats
                        </li>
                        <li className="flex items-start gap-3 text-slate-700 text-[15px]">
                            <span className="text-base mt-0.5">✨</span>
                            Compare discount percentages and offers
                        </li>
                        <li className="flex items-start gap-3 text-slate-700 text-[15px]">
                            <span className="text-base mt-0.5">✨</span>
                            Optimize pricing displays for maximum impact
                        </li>
                        <li className="flex items-start gap-3 text-slate-700 text-[15px]">
                            <span className="text-base mt-0.5">✨</span>
                            Find the sweet spot for revenue optimization
                        </li>
                    </ul>
                    <div className="w-full inline-flex items-center gap-1 px-5 py-2.5 rounded-full text-sm font-semibold bg-gradient-to-r from-purple-600 to-purple-400 text-white">
                        <DollarSign size={16} />
                        Increase revenue by 15-35%
                    </div>
                </div>

                {/* Product Page Elements Card */}
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 border-2 border-transparent hover:border-blue-600">
                    <div className="w-15 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-teal-600 to-cyan-500 mb-5 transition-transform duration-300 hover:scale-110">
                        <Palette size={28} className="text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-4">
                        Product Page Elements
                    </h3>
                    <ul className="space-y-2 mb-5 list-none p-0">
                        <li className="flex items-start gap-3 text-slate-700 text-[15px]">
                            <span className="text-base mt-0.5">✨</span>
                            Test button colors, sizes, and text
                        </li>
                        <li className="flex items-start gap-3 text-slate-700 text-[15px]">
                            <span className="text-base mt-0.5">✨</span>
                            Compare product descriptions and layouts
                        </li>
                        <li className="flex items-start gap-3 text-slate-700 text-[15px]">
                            <span className="text-base mt-0.5">✨</span>
                            Optimize image placements and galleries
                        </li>
                        <li className="flex items-start gap-3 text-slate-700 text-[15px]">
                            <span className="text-base mt-0.5">✨</span>
                            Improve add-to-cart conversion rates
                        </li>
                    </ul>
                    <div className=" w-full inline-flex items-center gap-1 px-5 py-2.5 rounded-full text-sm font-semibold bg-gradient-to-r from-teal-600 to-cyan-500 text-white">
                        <TrendingUp size={16} />
                        Boost conversions by 20-40%
                    </div>
                </div>

                {/* Cart & Checkout Flow Card */}
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 border-2 border-transparent hover:border-blue-600">
                    <div className="w-15 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-pink-600 to-pink-400 mb-5 transition-transform duration-300 hover:scale-110">
                        <ShoppingCart size={28} className="text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-4">
                        Cart & Checkout Flow
                    </h3>
                    <ul className="space-y-2 mb-5 list-none p-0">
                        <li className="flex items-start gap-3 text-slate-700 text-[15px]">
                            <span className="text-base mt-0.5">✨</span>
                            Test discount banners and urgency messages
                        </li>
                        <li className="flex items-start gap-3 text-slate-700 text-[15px]">
                            <span className="text-base mt-0.5">✨</span>
                            Optimize checkout process and form fields
                        </li>
                        <li className="flex items-start gap-3 text-slate-700 text-[15px]">
                            <span className="text-base mt-0.5">✨</span>
                            Compare shipping options and displays
                        </li>
                        <li className="flex items-start gap-3 text-slate-700 text-[15px]">
                            <span className="text-base mt-0.5">✨</span>
                            Reduce cart abandonment effectively
                        </li>
                    </ul>
                    <div className="w-full inline-flex items-center gap-1 px-5 py-2.5 rounded-full text-sm font-semibold bg-gradient-to-r from-pink-600 to-pink-400 text-white">
                        <Target size={16} />
                        Reduce abandonment by 25-45%
                    </div>
                </div>
            </div>

            {/* Impact Statistics */}
            <div className="bg-white rounded-2xl p-8 md:p-10 shadow-lg mb-8">
                <h3 className="flex flex-col md:flex-row items-center justify-center gap-3 text-2xl font-bold text-slate-800 mb-10 text-center">
                    <Lightbulb size={24} />
                    Why A/B Testing Works for CausalFunnel Clients
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-10 text-center">
                    <div className="flex flex-col items-center justify-start min-h-[120px]">
                        <div className="mb-4 flex justify-center items-center">
                            <BarChart3 size={32} className="text-blue-600" />
                        </div>
                        <span className="text-5xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3 block leading-tight py-2 min-h-[60px] flex items-center">
                            200M+
                        </span>
                        <div className="text-base text-slate-600 leading-snug">
                            Active Sessions analyzed across our platform to optimize conversions
                        </div>
                    </div>

                    <div className="flex flex-col items-center justify-start min-h-[120px]">
                        <div className="mb-4 flex justify-center items-center">
                            <Settings size={32} className="text-purple-600" />
                        </div>
                        <span className="text-5xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3 block leading-tight py-2 min-h-[60px] flex items-center">
                            50K+
                        </span>
                        <div className="text-base text-slate-600 leading-snug">
                            Tests Running simultaneously across all client stores
                        </div>
                    </div>

                    <div className="flex flex-col items-center justify-start min-h-[120px]">
                        <div className="mb-4 flex justify-center items-center">
                            <TrendingUp size={32} className="text-emerald-600" />
                        </div>
                        <span className="text-5xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3 block leading-tight py-2 min-h-[60px] flex items-center">
                            $100M+
                        </span>
                        <div className="text-base text-slate-600 leading-snug">
                            Revenue Impact generated through optimization campaigns
                        </div>
                    </div>
                </div>
            </div>

            {/* What's Happening Now */}
            <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
                <h3 className="flex flex-col md:flex-row items-center justify-center gap-3 text-2xl font-bold text-slate-800 mb-6 text-center">
                    <Rocket size={24} />
                    How to get Started
                </h3>

                <div className="space-y-0">
                    <div className="flex items-center gap-4 py-4 text-base text-slate-700 border-b border-slate-100">
                        <div className="w-3 h-3 rounded-full bg-blue-600 flex-shrink-0 animate-[pulseGlow_2s_infinite]"></div>
                        <span className="flex items-center gap-2">
                            <Target size={16} />
                            Choose what to test (pricing, buttons, layouts)
                        </span>
                    </div>

                    <div className="flex items-center gap-4 py-4 text-base text-slate-700 border-b border-slate-100">
                        <div className="w-3 h-3 rounded-full bg-blue-600 flex-shrink-0 animate-[pulseGlow_2s_infinite]"></div>
                        <span className="flex items-center gap-2">
                            <Settings size={16} />
                            Configure your test variants in under 5 minutes
                        </span>
                    </div>

                    <div className="flex items-center gap-4 py-4 text-base text-slate-700 border-b border-slate-100">
                        <div className="w-3 h-3 rounded-full bg-amber-500 flex-shrink-0 animate-[pulseGlow_2s_infinite]"></div>
                        <span className="flex items-center gap-2">
                            <Clock size={16} />
                            Launch your test and start collecting data immediately
                        </span>
                    </div>

                    <div className="flex items-center gap-4 py-4 text-base text-slate-700">
                        <div className="w-3 h-3 rounded-full bg-emerald-600 flex-shrink-0 animate-[pulseGlow_2s_infinite]"></div>
                        <span className="flex items-center gap-2">
                            <BarChart3 size={16} />
                            Get results and implement winning variants for higher revenue
                        </span>
                    </div>
                </div>
            </div>

            {/* Quick Start Examples */}
            <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
                <h3 className="flex flex-col md:flex-row items-center justify-center gap-3 text-2xl font-bold text-slate-800 mb-6 text-center">
                    <Lightbulb size={24} />
                    Popular First Tests That Drive Results
                </h3>

                <div className="space-y-0">
                    <div className="flex items-center gap-4 py-4 text-base text-slate-700 border-b border-slate-100">
                        <div className="w-3 h-3 rounded-full bg-emerald-600 flex-shrink-0 animate-[pulseGlow_2s_infinite]"></div>
                        <span className="flex items-center gap-2">
                            <Target size={16} />
                            "Buy Now" vs "Add to Cart" button text (+18% conversion)
                        </span>
                    </div>

                    <div className="flex items-center gap-4 py-4 text-base text-slate-700 border-b border-slate-100">
                        <div className="w-3 h-3 rounded-full bg-emerald-600 flex-shrink-0 animate-[pulseGlow_2s_infinite]"></div>
                        <span className="flex items-center gap-2">
                            <DollarSign size={16} />
                            "$29.99" vs "$30" pricing format (+12% revenue)
                        </span>
                    </div>

                    <div className="flex items-center gap-4 py-4 text-base text-slate-700 border-b border-slate-100">
                        <div className="w-3 h-3 rounded-full bg-emerald-600 flex-shrink-0 animate-[pulseGlow_2s_infinite]"></div>
                        <span className="flex items-center gap-2">
                            <Palette size={16} />
                            Red vs Blue vs Green checkout button color (+25% clicks)
                        </span>
                    </div>

                    <div className="flex items-center gap-4 py-4 text-base text-slate-700">
                        <div className="w-3 h-3 rounded-full bg-emerald-600 flex-shrink-0 animate-[pulseGlow_2s_infinite]"></div>
                        <span className="flex items-center gap-2">
                            <ShoppingCart size={16} />
                            "Free Shipping" vs "Fast Delivery" messaging (+31% cart completion)
                        </span>
                    </div>
                </div>
            </div>

            {/* New CTA Section with black background */}
            <div className="bg-black rounded-2xl p-8 md:p-10 text-center mt-12 relative overflow-hidden">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-full text-sm font-medium mb-8">
                    <Zap size={16} className="text-white" />
                    <span>Create Your Test</span>
                </div>

                <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
                    Stop Guessing. <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">Start Converting.</span>
                </h2>

                <p className="text-lg text-gray-300 mb-4 leading-relaxed max-w-2xl mx-auto">
                    CausalFunnel's A/B testing platform helps you test different versions of your store to find what converts best and drives more sales.
                </p>

                <p className="text-base text-gray-400 mb-8 font-medium">
                    Customers see up to 75% lift in conversions within weeks.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10 mb-8 max-w-4xl mx-auto">
                    <div className="bg-gray-900 p-6 rounded-2xl border border-gray-800">
                        <div className="mb-4 flex justify-center">
                            <TrendingUp size={24} className="text-gray-500" />
                        </div>
                        <div className="text-3xl font-black text-white mb-2">
                            Up to 75%
                        </div>
                        <div className="text-sm text-gray-500 leading-snug">
                            Average Conversion Lift
                        </div>
                    </div>

                    <div className="bg-gray-900 p-6 rounded-2xl border border-gray-800">
                        <div className="mb-4 flex justify-center">
                            <Clock size={24} className="text-gray-500" />
                        </div>
                        <div className="text-3xl font-black text-white mb-2">
                            2 Weeks
                        </div>
                        <div className="text-sm text-gray-500 leading-snug">
                            Average Time To See Results
                        </div>
                    </div>

                    <div className="bg-gray-900 p-6 rounded-2xl border border-gray-800">
                        <div className="mb-4 flex justify-center">
                            <Settings size={24} className="text-gray-500" />
                        </div>
                        <div className="text-3xl font-black text-white mb-2">
                            Zero
                        </div>
                        <div className="text-sm text-gray-500 leading-snug">
                            Setup Required
                        </div>
                    </div>
                </div>

                <Link 
                    to="/create-element-test" 
                    className="inline-block bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white px-9 py-4 rounded-full text-lg font-bold shadow-lg shadow-blue-500/40 hover:shadow-xl hover:shadow-blue-500/60 hover:-translate-y-1 transition-all duration-300"
                >
                    Create Your Test Now
                </Link>
            </div>

            <style jsx>{`
                @keyframes gradientShift {
                    0%, 100% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                }
                
                @keyframes flashyPulse {
                    0%, 100% {
                        opacity: 1;
                        transform: scale(1);
                        box-shadow: 0 8px 30px rgba(37, 99, 235, 0.4);
                    }
                    50% {
                        opacity: 0.9;
                        transform: scale(1.05);
                        box-shadow: 0 15px 50px rgba(37, 99, 235, 0.8);
                    }
                }
                
                @keyframes pulseGlow {
                    0%, 100% { 
                        opacity: 1; 
                        transform: scale(1);
                        box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
                    }
                    50% { 
                        opacity: 0.8; 
                        transform: scale(1.1);
                        box-shadow: 0 0 0 10px rgba(16, 185, 129, 0);
                    }
                }
            `}</style>
        </div>
    );
};

export default ABTestDataCollection;